import {
  AdminDeviceSessionSchema,
  AdminDeviceSessionsRevokeAllResultSchema,
  type AdminDeviceSession,
  type AdminDeviceSessionsRevokeAllResult,
} from '@carsfinders/contracts';

import { apiFetch } from '../../lib/api';

/**
 * Superadmin control of the admin app's device sessions (admin-mobile-app
 * phase 3, `admin-device-sessions.controller.ts`). Every answer is the
 * `{ success, data }` envelope; the data is parsed with the contract schema so
 * a drifted backend fails loudly here instead of rendering half a row.
 *
 *   GET  /api/admin/mobile-sessions[?active=true|false]          → AdminDeviceSession[]
 *   POST /api/admin/mobile-sessions/:id/revoke                   → { revoked: true }
 *   POST /api/admin/users/:userId/mobile-sessions/revoke-all     → { revoked: n }
 *
 * The POSTs go through `apiFetch`, which attaches the double-submit CSRF token.
 */
interface Envelope<T> {
  success?: boolean;
  data?: T;
}

const SessionListSchema = AdminDeviceSessionSchema.array();

export function fetchMobileSessions(
  filter: { activeOnly?: boolean } = {},
): Promise<AdminDeviceSession[]> {
  return apiFetch<Envelope<unknown>>('admin/mobile-sessions', {
    searchParams: { active: filter.activeOnly ? 'true' : undefined },
  }).then((response) => SessionListSchema.parse(response?.data ?? []));
}

export function revokeMobileSession(id: string): Promise<{ revoked: boolean }> {
  return apiFetch<Envelope<{ revoked: boolean }>>(
    `admin/mobile-sessions/${encodeURIComponent(id)}/revoke`,
    { method: 'POST' },
  ).then((response) => ({ revoked: response?.data?.revoked === true }));
}

export function revokeAllMobileSessions(
  userId: string,
): Promise<AdminDeviceSessionsRevokeAllResult> {
  return apiFetch<Envelope<unknown>>(
    `admin/users/${encodeURIComponent(userId)}/mobile-sessions/revoke-all`,
    { method: 'POST' },
  ).then((response) => AdminDeviceSessionsRevokeAllResultSchema.parse(response?.data));
}
